import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Leaf, Truck, ShieldCheck, Star, Quote } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1783764245498-31991c8a6cae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxibG9jayUyMHByaW50JTIwZmFicmljfGVufDB8fHx8MTc4Njc5MDU2MHww&ixlib=rb-4.1.0&q=85";
const features = [
  { Icon: Sparkles, title: "Hand Block-Printed", desc: "Every motif stamped by artisan hands" },
  { Icon: Leaf, title: "Natural Dyes", desc: "Earth-drawn colours, pure cotton" },
  { Icon: Truck, title: "Ships Across India", desc: "Fast, tracked delivery to your door" },
  { Icon: ShieldCheck, title: "COD & Secure Pay", desc: "UPI, cards or cash on delivery" },
];
const ABOUT_IMG = "https://images.unsplash.com/photo-1695956353120-54ce5e91632b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHx1ZGFpcHVyJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4Njc5MDU2MHww&ixlib=rb-4.1.0&q=85";
const TEXTURE = "https://images.unsplash.com/photo-1773846012458-e6a66c26e49f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxibG9jayUyMHByaW50JTIwZmFicmljfGVufDB8fHx8MTc4Njc5MDU2MHww&ixlib=rb-4.1.0&q=85";

const heroLines = ["Hand block-printed", "Sanganeri shirts", "for men & little ones"];

const chapters = [
  { n: "01", title: "The Block", body: "Every motif begins as a hand-carved teak block, its floral trails traced by artisans whose families have printed in Sanganer for generations." },
  { n: "02", title: "The Dye", body: "We use natural, earth-drawn colours — madder red, indigo blue, turmeric gold — fixed the slow way, so the cloth breathes and ages beautifully." },
  { n: "03", title: "The Shirt", body: "Cut for real days in the Rajasthan sun. Soft pure cotton, honest stitching, sizes for grown men and the smallest explorers alike." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [heroImg, setHeroImg] = useState(DEFAULT_HERO);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
    api.get("/config").then((r) => { if (r.data.hero_image) setHeroImg(imageUrl(r.data.hero_image)); }).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] overflow-hidden bg-[hsl(var(--secondary))]">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={heroImg} alt="Sanganeri block print" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--secondary))] via-[hsl(var(--secondary)/0.35)] to-transparent" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="overline text-[hsl(var(--accent))] mb-6" data-testid="hero-overline">
            Navrang Udaipur — Est. Rajasthan
          </motion.p>
          <h1 data-testid="hero-heading" className="font-display text-white font-500 leading-[0.92] text-5xl sm:text-7xl lg:text-8xl max-w-5xl">
            {heroLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span className="block"
                  initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ delay: 0.35 + i * 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] }}>
                  {i === 2 ? <span className="italic text-[hsl(var(--accent))]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-5">
            <Link to="/shop" data-testid="hero-shop-btn"
              className="group inline-flex items-center gap-3 bg-[hsl(var(--primary))] text-white px-8 py-4 font-body text-sm tracking-wide hover:bg-white hover:text-[hsl(var(--secondary))] transition-colors duration-400">
              Shop the collection <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </Link>
            <Link to="/about" className="text-white/80 font-body text-sm link-underline">Our craft →</Link>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-3 text-white/60">
          <span className="overline">Scroll</span>
          <span className="w-10 h-px bg-white/40 relative overflow-hidden">
            <span className="absolute inset-0 bg-white animate-pulse" />
          </span>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee words={["Sanganeri", "Bagru", "Pure Cotton", "Natural Dyes", "Handmade in Udaipur"]} className="bg-background" />

      {/* FEATURES STRIP */}
      <section className="border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.06} className="bg-background p-8 flex flex-col items-start gap-3">
              <f.Icon className="w-7 h-7 text-[hsl(var(--primary))]" strokeWidth={1.4} />
              <div>
                <p className="font-display text-xl leading-tight">{f.title}</p>
                <p className="font-body text-xs text-muted-foreground mt-1">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <p className="overline text-[hsl(var(--primary))] mb-6">Shop by category</p>
          <h2 className="font-display text-4xl md:text-6xl max-w-3xl leading-tight">Two collections, one <span className="italic text-[hsl(var(--primary))]">timeless craft.</span></h2>
        </Reveal>
        <div className={`grid gap-6 mt-14 ${categories.length > 2 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          {categories.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1}>
              <Link to={`/shop?category=${c.slug}`} data-testid={`category-card-${c.slug}`} className="group block relative overflow-hidden aspect-[4/5] bg-muted">
                <img src={c.image ? imageUrl(c.image) : DEFAULT_HERO} alt={c.name} loading="lazy" decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8 md:p-10 text-white">
                  <h3 className="font-display text-4xl md:text-5xl capitalize">{c.name}</h3>
                  <span className="mt-5 inline-flex items-center gap-2 overline group-hover:gap-4 transition-all">Explore <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40">
        <Reveal>
          <p className="overline text-[hsl(var(--primary))] mb-6">The Manifesto</p>
          <h2 className="font-display text-4xl md:text-6xl max-w-4xl leading-tight">Three hands touch every shirt before it reaches yours.</h2>
        </Reveal>
        <div className="mt-20 grid md:grid-cols-3 gap-px bg-border border border-border">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1} className="bg-background p-8 md:p-12">
              <span className="font-mono-label text-[hsl(var(--primary))] text-sm">({c.n})</span>
              <h3 className="font-display text-3xl mt-6">{c.title}</h3>
              <p className="font-body text-sm text-muted-foreground mt-4 leading-relaxed">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="flex items-end justify-between mb-12 border-b border-border pb-6">
          <Reveal><h2 className="font-display text-4xl md:text-5xl">Featured pieces</h2></Reveal>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 font-body text-sm link-underline" data-testid="view-all-btn">
            View all <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="featured-grid">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* HERITAGE SPLIT */}
      <section className="grid md:grid-cols-2 bg-[hsl(var(--secondary))] text-white">
        <div className="relative overflow-hidden min-h-[60vh] md:min-h-[80vh]">
          <img src={ABOUT_IMG} alt="Udaipur heritage" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="flex flex-col justify-center p-8 md:p-20">
          <Reveal>
            <p className="overline text-[hsl(var(--accent))] mb-6">From the City of Lakes</p>
            <h2 className="font-display text-4xl md:text-6xl leading-tight">A craft born in <span className="italic text-[hsl(var(--accent))]">Udaipur's</span> quiet lanes.</h2>
            <p className="font-body text-white/70 mt-8 max-w-md leading-relaxed">
              Navrang means nine colours — a nod to the vivid palette of Rajasthan. We work directly with block-printing families,
              keeping a centuries-old craft alive one honest shirt at a time.
            </p>
            <Link to="/about" data-testid="heritage-btn"
              className="mt-10 inline-flex items-center gap-3 border border-white/40 text-white px-8 py-4 font-body text-sm hover:bg-white hover:text-[hsl(var(--secondary))] transition-colors duration-400">
              Read our story <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {reviews.length > 0 && (
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <p className="overline text-[hsl(var(--primary))] mb-6">Loved across India</p>
          <h2 className="font-display text-4xl md:text-6xl max-w-3xl leading-tight">What our customers say.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-px bg-border border border-border mt-14">
          {reviews.slice(0, 6).map((t, i) => (
            <Reveal key={t.id} delay={i * 0.08} className="bg-background p-8 md:p-10 flex flex-col">
              <Quote className="w-8 h-8 text-[hsl(var(--accent))]" strokeWidth={1.4} />
              <div className="flex gap-1 mt-4">
                {[...Array(Math.max(1, Math.min(5, t.rating || 5)))].map((_, s) => <Star key={s} className="w-4 h-4 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" />)}
              </div>
              <p className="font-body text-sm text-foreground/80 mt-5 leading-relaxed flex-1">"{t.text}"</p>
              <div className="mt-6 pt-5 border-t border-border">
                <p className="font-display text-xl">{t.name}</p>
                {t.city && <p className="overline text-muted-foreground mt-1">{t.city}</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden py-28 md:py-40 text-center">
        <img src={TEXTURE} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="relative max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="font-display text-5xl md:text-7xl leading-tight">Dress in colour.<br /><span className="italic text-[hsl(var(--primary))]">Dress in craft.</span></h2>
            <Link to="/shop" data-testid="cta-shop-btn"
              className="mt-10 inline-flex items-center gap-3 bg-[hsl(var(--primary))] text-white px-10 py-4 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors duration-400">
              Explore the collection <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
